/** Deployed environments that should follow production security and CMS behavior. */
export const PROD_MIRROR_ENVS = ['prod', 'preview', 'shadow', 'test-www'] as const;

export type ProdMirrorEnv = (typeof PROD_MIRROR_ENVS)[number];

const PROD_MIRROR_ENV_SET: ReadonlySet<string> = new Set(PROD_MIRROR_ENVS);

/** Whether a `CURRENT_ENV` value behaves like production (excluding strict SEO indexing). */
export function envMirrorsProd(env: string): boolean {
    return PROD_MIRROR_ENV_SET.has(env);
}

/** Whether the active environment behaves like production (excluding strict SEO indexing). */
export function currentEnvMirrorsProd(): boolean {
    const env = getCurrentEnv();
    return env !== undefined && envMirrorsProd(env);
}

/**
 * Set CURRENT_ENV on window object (as a read-only property in prod-like envs)
 * This prevents client-side code from modifying the environment value
 *
 * @param env - The environment value to set (e.g., 'prod', 'local', 'platypus')
 */
export function setCurrentEnvOnWindow(env: string): void {
    if (typeof window === 'undefined') {
        return; // Server-side, no need to set
    }

    // If already set and locked, don't override
    if (window.__CURRENT_ENV__ !== undefined) {
        return;
    }

    // Define as read-only, non-configurable property
    // This prevents modification or deletion after initialization
    const isLocked = envMirrorsProd(env);
    Object.defineProperty(window, '__CURRENT_ENV__', {
        value: env,
        writable: !isLocked,
        configurable: !isLocked,
        enumerable: !isLocked,
    });
}

/**
 * Get CURRENT_ENV value safely from client or server
 *
 * On server: Uses process.env.CURRENT_ENV
 * On client: Uses window.__CURRENT_ENV__ (set in _app.tsx from getInitialProps)
 *
 * When running in development (e.g. turbo dev) without CURRENT_ENV set,
 * falls back to 'local' so isDevEnv() and other env checks behave correctly.
 *
 * This replaces direct access to process.env.CURRENT_ENV in client-side code
 */
export function getCurrentEnv(): string | undefined {
    // Always check process.env first if available (works in both server and test environments)
    // In Jest/test environments, window might be defined (jsdom), but we still want to use process.env
    if (typeof process !== 'undefined' && process.env?.CURRENT_ENV) {
        return process.env.CURRENT_ENV;
    }

    // Client-side: use window.__CURRENT_ENV__ set from getInitialProps
    // Only check window if process.env.CURRENT_ENV is not available
    if (typeof window !== 'undefined' && window.__CURRENT_ENV__) {
        return window.__CURRENT_ENV__;
    }

    // Dev fallback: when running under turbo dev / next dev, CURRENT_ENV may not be in env;
    // treat as local so isDevEnv() and dev-only behavior work without extra config
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        return 'local';
    }

    // Prod fallback: when CURRENT_ENV is unset in production-like setups (e.g. NODE_ENV === 'production'),
    // treat as prod so security-sensitive features (e.g. introspection) require the secret by default
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
        return 'prod';
    }

    return undefined;
}

/**
 * Check if current environment is production
 */
export function isProd(): boolean {
    return getCurrentEnv() === 'prod';
}

/**
 * Check if current environment is a dev environment (local or platypus)
 */
export function isDevEnv(): boolean {
    const env = getCurrentEnv();
    return env === 'local' || env === 'platypus';
}

export function isLocalEnv(): boolean {
    return getCurrentEnv() === 'local';
}

/**
 * Check if this process is running a development build (`next dev`).
 */
export function isDevBuild(): boolean {
    return typeof process !== 'undefined' && process.env?.NODE_ENV === 'development';
}

/**
 * Check if this process is running local HTTPS dev (`pnpm dev:ssl`).
 *
 * `scripts/ssl.mjs` sets `LOCAL_HTTPS=true` for server/build-time code;
 * `NEXT_PUBLIC_LOCAL_HTTPS` covers client bundles (inlined at build time).
 */
export function isLocalHttps(): boolean {
    return (
        typeof process !== 'undefined' &&
        (process.env?.NEXT_PUBLIC_LOCAL_HTTPS || process.env?.LOCAL_HTTPS) === 'true'
    );
}

declare global {
    interface Window {
        __CURRENT_ENV__?: string;
    }
}
